import { TrainingLevel } from "../../shared/playerAccount";
import { ArrayVec2 } from "../../shared/synchronization";
import { GameClient } from "./gameClient";

export enum IngameTutorialStep {
	Welcome,
	Viruses,
	BloodCells,
	Market,
	Purchase,
	Purchase2,

	Rotate,

	Gold,
	UnspentGold,
	NextRound,

	StateReset,
	MoneyReset,
	Population,
	Objective,
	Experience,
	BuyingExp,

	LevelUp,
	LevelUp2,

	VirusGrowth,
	CameraOverVirus,
	CameraOverVirus2,
	CameraOverVirus3,
	VirusInformation,


	Selling,
	Refresh,
	Freeze,
	HaveFun,

	TrainingLevel2,
	TrainingLevel2Turns,
	CombinePrompt,
	CombineInstruction,
	Combining,
	Elites,
	RewardCell,
	RewardCell2,

	Capacity1,
	Capacity2,
	Capacity3,
	Capacity4,

	//Multiplayer
	MultiplayerIntro,
	MultiplayerIntro2,
	MultiplayerIntro3,
	MultiplayerIntro4,
	MultiplayerIntro5,
	SharedSpace,
	SharedLane,
	BattleRounds1,
	BattleRounds2,
	BattleRound3,
	MultiplayerWinCondition,

	Tiebreaker,
	Tiebreaker2,
	Tiebreaker3,

	ReportBugs, //playtest builds only
}

export enum Position {
	Center,
	Left,
	Right
}

export interface IngameTutorialData {
	x: number;
	y: number;
	text: string;
	radius: number;
	vOffset: number;
	requireClick?: boolean;
}

type TutorialStateOptions = {
	disableCellClicks?: boolean,
	enabledButtons?: string,
	cameraFocus?: "player" | "enemy" | "infected" | "bot" | "middle" | "none",
	forceSellButtonShown?: boolean,
	hideSellButton?: boolean,
	forceCellDescriptionShown?: boolean,
	flashBugReportButton?: boolean,
}

class TutorialNode {
	step: IngameTutorialStep;
	text: string;
	offset: number | null;
	radius: number;
	fixedPosition: ArrayVec2 = [0, 0];
	hasDynamicPosition: boolean = true
	requiresClick: boolean = true;
	nextState: TutorialNode | null = null;
	options: TutorialStateOptions = {};

	getPosition = (gameClient: GameClient): ArrayVec2 => {
		return this.fixedPosition;
	}

	constructor(
		step: IngameTutorialStep,
		text: string,
		radius: number,
		offset: number | null,
		position: ArrayVec2 | ((gameClient: GameClient) => ArrayVec2),
		options?: TutorialStateOptions,
	) {
		this.step = step;
		this.text = text;
		this.radius = radius;
		this.offset = offset;
		this.fixedPosition = typeof position === 'function' ? [0, 0] : position;
		if (typeof position === 'function') {
			this.getPosition = position;
			this.hasDynamicPosition = true;
		}
		else {
			this.fixedPosition = position;
		}

		if (!options) {
			options = {};
		}
		this.options = options;
	}
}

type TutorialEvent = {
	event: string,
	node: TutorialNode | null,
	level?: TrainingLevel[],
	round?: number
	precedingEvent?: IngameTutorialStep;
	//If true, this event will only trigger once per game session, resets on game restart
	//Could also put this in user settings, but player might forget if they are coming back to tutorial.
	//Idk, arguable
	onlyOncePerSession?: boolean;
}

let seenTutorialsThisSession: Set<string> = new Set();

export class TutorialGraph {
	active: TutorialNode | null = null;
	events: TutorialEvent[] = [];

	constructor() { }

	event(event: string, level: TrainingLevel, round: number) {
		for (const tutEvent of this.events) {
			if (!tutEvent.node) continue; // Skip if no node is defined
			const matchesEvent = tutEvent.event === event;
			const matchesLevel = !tutEvent.level || tutEvent.level.length === 0 || tutEvent.level.includes(level);
			const matchesRound = !tutEvent.round || tutEvent.round === round;
			const matchesPreceding = !tutEvent.precedingEvent || (this.active && this.active.step === tutEvent.precedingEvent);
			const notExhausedThisSession = !tutEvent.onlyOncePerSession || !seenTutorialsThisSession.has(event);
			if (matchesEvent && matchesLevel && matchesRound && matchesPreceding && notExhausedThisSession) {
				const node = tutEvent.node;
				this.active = node;
				seenTutorialsThisSession.add(event);
				return node;
			}
		}
		return null;
	}
}

export class TutorialGraphBuilder {
	graph: TutorialGraph = new TutorialGraph();
	active: TutorialNode | null = null;
	lastCreatedEvent: TutorialEvent | null = null;
	constructor() {
	}

	on(event: string, levels: TrainingLevel[], when: number | "oncePerSession") {
		this.active = null;
		this.addEvent(event, levels, when);
		return this;
	}

	private addEvent(event: string, level: TrainingLevel[], when: number | "oncePerSession" | undefined = undefined) {
		const round = typeof when === 'number' ? when : undefined;
		this.lastCreatedEvent = { event, node: null, level, round };
		if (when === "oncePerSession") {
			this.lastCreatedEvent.onlyOncePerSession = true;
		}
		this.graph.events.push(this.lastCreatedEvent);
		return this.lastCreatedEvent;
	}

	next(step: IngameTutorialStep,
		text: string,
		radius: number,
		offset: number | null,
		position: ArrayVec2 | ((game: GameClient) => ArrayVec2),
		//options?: TutorialStateOptions
	): TutorialGraphBuilder {
		const nextState = new TutorialNode(step, text, radius, offset, position);
		if (this.active) {
			this.active.nextState = nextState;
		}
		else {
			//Find last created event
			if (this.lastCreatedEvent) {
				this.lastCreatedEvent.node = nextState;
			}
			else {
				throw new Error("No active state to link next state to");
			}
		}
		this.active = nextState;
		return this;
	}

	wait() {
		this.active!.nextState = null;
		return this;
	}

	waitFor(event: string) {
		if (!this.active) {
			throw new Error("Cannot wait for event without an active state");
		}
		this.active.requiresClick = false;
		const added = this.addEvent(event, [], undefined);
		added.precedingEvent = this.active.step;
		this.active = null;
		return this;
	}

	cameraFocus(focus: "player" | "enemy" | "infected" | "bot" | "none" | "middle") {
		if (!this.active) {
			throw new Error("Cannot set camera focus without an active state");
		}
		this.active.options.cameraFocus = focus;
		return this;
	}

	enableCellClicks() {
		if (!this.active) {
			throw new Error("Cannot enable cell clicks without an active state");
		}
		this.active.options.disableCellClicks = false;
		return this;
	}

	enableButtons(button: "immuneCell" | "experience" | "none") {
		if (!this.active) {
			throw new Error("Cannot enable buttons without an active state");
		}
		this.active.options.enabledButtons = button;
		return this;
	}

	flashBugReportButton() {
		if (!this.active) {
			throw new Error("Cannot flash the bug report button without an active state");
		}
		this.active.options.flashBugReportButton = true;
		return this;
	}

	forceShowSellButton() {
		if (!this.active) {
			throw new Error("Cannot force sell button without an active state");
		}
		this.active.options.forceSellButtonShown = true;
		return this;
	}

	forceShowCellDescription() {
		if (!this.active) {
			throw new Error("Cannot force cell description without an active state");
		}
		this.active.options.forceCellDescriptionShown = true;
		return this;
	}

	hideSellButton() {
		if (!this.active) {
			throw new Error("Cannot hide sell button without an active state");
		}
		this.active.options.hideSellButton = true;
		return this;
	}
}